import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  BlockingLoadingSpinnerComponent,
  showRequestErrors,
  HeaderComponent,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { MessageBarStore, SOFTLINE_FEATURE_MESSAGE_BAR, RepeatDirective, TranslatePipe } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import {
  SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
  SOFTLINE_FEATURE_UMBUCHUNGSARTIKEL,
} from '../../lagerkorrektur-artikel.shared';
import { LagerkorrekturArtikelStore, UmbuchungsartikelStore } from '../../store';
import { Artikel } from '@softapps/lager/core';
import { BehaviorSubject } from 'rxjs';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';

@Component({
  selector: 'soft-umbuchungsartikel-auswahl',
  imports: [
    CommonModule,
    ProgressStepsComponent,
    BlockingLoadingSpinnerComponent,
    HeaderComponent,
    RepeatDirective,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
  ],
  templateUrl: './umbuchungsartikel-auswahl.page.html',
  styleUrls: ['./umbuchungsartikel-auswahl.page.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UmbuchungsartikelAuswahlPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  loading$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);
  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.lagerplatz
  );
  artikel$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.artikel
  );
  umbuchungsartikel$ = this.store.observe(
    SOFTLINE_FEATURE_UMBUCHUNGSARTIKEL,
    UmbuchungsartikelStore.getters.all
  );

  readonly steps: ProgressStep[] = [
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.DESCRIPTION',
    },
  ];

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERKORREKTUR_ARTIKEL.TITLE'
    );
    this.loading$.next(true);
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_UMBUCHUNGSARTIKEL,
        UmbuchungsartikelStore.actions.loadManyOnce
      );
      this.loading$.next(false);
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    }
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel/mengen-auswahl']);
  }

  async onSelect(umbuchungsartikel: Artikel): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.mutations.setUmbuchungsartikel,
      umbuchungsartikel
    );
    const lagerplatz = this.store.get(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.getters.lagerplatz
    );
    const inhalt = this.store.get(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.getters.artikel
    );
    this.saving$.next(true);
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
        LagerkorrekturArtikelStore.actions.lagerkorrektur,
        {
          lagerplatzInhalt: {
            lagerplatz: lagerplatz.lagerplatz,
            inhalt: [inhalt],
          },
          umbuchungsartikel,
        }
      );
      this.saving$.next(false);
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#LAGERKORREKTUR_ARTIKEL.MESSAGES.SUCCESS.KORREKTUR'
      );
      await this.router.navigate(['/lagerkorrektur-artikel']);
    } catch (e) {
      this.saving$.next(false);
      showRequestErrors(this.store, e);
    }
  }
}
